import { PlusCircleOutlined } from '@ant-design/icons';
import { useMount } from 'ahooks';
import { Form, FormInstance, Modal, Tag } from 'antd';
import { NamePath } from 'antd/lib/form/interface';
import { RevxDateWeek } from 'components';
import { EditIcon } from 'components/Icons';
import { RevxButton } from 'components/RevxButton';
import { RevxDate } from 'components/RevxDate';
import { RevxInputNumber } from 'components/RevxInputNumber';
import { RevxMoney } from 'components/RevxMoney';
import {
  SectionHeading,
  TextHeavy,
  UnderlineText,
} from 'components/RevxTypography';
import { ShippingAccountTypeEnum } from 'domains/jobconfigDomain';
import {
  PurchaseOrderJob,
  PurchaseOrderJobShippingItem,
  ShipmentTypeEnum,
} from 'domains/orderDomain';
import { CarrierTypeEnum, ShippingSplitPayload } from 'domains/shippingDomain';
import {
  TemporaryShippingAddress,
  TemporaryShippingAddressValue,
} from 'features/ShoppingCart/components/TemporaryShippingAddress';
import {
  TemporaryShippingUps,
  TemporaryShippingUpsValue,
} from 'features/ShoppingCart/components/TemporaryShippingUps';
import { useDemoFeatureFlags, useUser } from 'hooks';
import { cloneDeep } from 'lodash-es';
import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import workspaceDetailService from 'services/workspaceDetailService';
import './index.less';

const { confirm } = Modal;

type RevxShippingFormProps = {
  form: FormInstance;
  shippingList: PurchaseOrderJob[];
  hideAction?: boolean;
  onChange?: (x: ShippingSplitPayload) => Promise<string>;
  onEditStatusChange?: (x: boolean) => void;
};

type IFormData = { [x: string]: PurchaseOrderJobShippingItem[] };

type IFormQuantity = { [x: string]: number };

export const RevxShippingForm = (props: RevxShippingFormProps) => {
  const { form, shippingList, hideAction, onChange, onEditStatusChange } =
    props;
  const [formList, setFormList] = useState<PurchaseOrderJob[]>([]);
  const [orderEditList, setOrderEditList] = useState<number[]>([]);
  const [cacheOrderList, setCacheOrderList] = useState<PurchaseOrderJob[]>([]);
  const [formQuantityValues, setFormQuantityValues] = useState<IFormQuantity>(
    {}
  );
  const { t } = useTranslation();
  const user = useUser();
  const { transformValue } = useDemoFeatureFlags();

  const [carrierType, setCarrierType] = useState<CarrierTypeEnum[]>();
  const [checkOrderId, setCheckOrderId] = useState<number | undefined>();

  const carrierService = [
    'UPS Next Day Air Early',
    'UPS Next Day Air',
    'UPS Next Day Air Saver',
    'UPS 2nd Day Air',
    'UPS 3 Day Select',
    'UPS Ground',
  ];

  const accountTypeList = [
    {
      name: 'Organization',
      value: ShippingAccountTypeEnum.ORGANIZATION,
    },
    {
      name: 'Revshop',
      value: ShippingAccountTypeEnum.REVSHOP,
    },
    {
      name: 'Supplier',
      value: ShippingAccountTypeEnum.SUPPLIER,
    },
  ];

  const otherCarrierService = [
    'UPS Freight LTL',
    // 'UPS Next Day Air® Freight',
    // 'UPS 2nd Day Air® Freight',
    // 'UPS 3 Day FreightSM',
    // 'UPS Next Day Air® Freight NGS',
    // 'UPS 2nd Day Air® Freight NGS',
    // 'UPS 3 Day FreightSM NGS',
  ];

  useMount(async () => {
    const carrierTypeRes = await workspaceDetailService.shippingCarriers();
    setCarrierType(carrierTypeRes);
  });

  const defaultShippingLineItem: PurchaseOrderJobShippingItem = {
    carrierService: '',
    carrierType: CarrierTypeEnum.UPS,
    name: '',
    quantity: undefined,
    shipmentType: ShipmentTypeEnum.PACKAGE,
    weight: 0,
    costLineItems: {
      freightCharge: 0,
      printingCharge: 0,
      totalCharge: 0,
      revxFreightCharge: 0,
      revxPrintingCharge: 0,
      revxTotalCharge: 0,
      shippingCost: 0,
    },

    shippingInfo: {
      toAddressLine: '',
      toCity: '',
      toCountryCode: '',
      toName: '',
      toPostalCode: '',
      toStateProvinceCode: '',
    },
  };

  const defaultValidate = ['quantity', 'shippingInfo', 'carrier'];

  const setFormData = useCallback(
    (list: PurchaseOrderJob[]) => {
      const formData: IFormData = {};
      list.forEach((item) => {
        formData[item['jobId']] = item.shippingItems;
      });
      form.setFieldsValue(formData);
      setFormList(list);
    },
    [form, setFormList]
  );

  useEffect(() => {
    setFormData(shippingList);
  }, [shippingList, setFormData]);

  useEffect(() => {
    if (typeof onEditStatusChange === 'function') {
      onEditStatusChange(!!orderEditList.length);
    }
  }, [orderEditList, onEditStatusChange]);

  const deleteItemLine = (
    orderId: number,
    orderIdx: number,
    shippingIdx: number
  ) => {
    const formListClone = cloneDeep(formList);
    const validateArr = lineItemValidate(orderId, orderIdx);
    const formRes = form.getFieldsValue(validateArr);
    formListClone[orderIdx].shippingItems.map(
      (item, idx, arr) => (arr[idx] = { ...item, ...formRes[+orderId][idx] })
    );
    formListClone[orderIdx].shippingItems.splice(shippingIdx, 1);
    setFormData(formListClone);

    const allValue = form.getFieldsValue();
    formQuantityMemo(allValue, formListClone);
  };

  const isEdit = (orderIdx: number) => {
    return !!orderEditList.filter((item) => item === orderIdx).length;
  };

  const shippingEditCancel = (orderId: number) => {
    setOrderEditList(orderEditList.filter((item) => !(item === orderId)));
  };

  const editLineItem = (orderId: number) => {
    setOrderEditList([...orderEditList, orderId]);
    const cacheOrder = cloneDeep(
      formList.filter((item) => item.jobId === orderId)[0]
    );
    setCacheOrderList([...cacheOrderList, cacheOrder]);
  };

  const lineItemValidate = (orderId: number, orderIdx: number) => {
    const validateArr: NamePath[] = [];
    for (let i = 0; i < formList[orderIdx].shippingItems.length; i++) {
      // defaultValidate[0] = orderId,
      // defaultValidate[1] = i;
      const lineItemValidate: any = [];
      lineItemValidate.push(orderId);
      lineItemValidate.push(i);
      defaultValidate.forEach((item) => {
        const outLineItemValidate = cloneDeep(lineItemValidate);
        outLineItemValidate.push(item);
        validateArr.push(cloneDeep(outLineItemValidate));
      });
    }
    return validateArr;
  };

  const cancelItemLine = (orderId: number, orderIdx: number) => {
    const formListClone = cloneDeep(formList);
    const validateArr = lineItemValidate(orderId, orderIdx);
    form.resetFields(validateArr);
    const cacheOrder = cacheOrderList.filter(
      (item) => item.jobId === orderId
    )[0];

    formListClone[orderIdx] = cacheOrder;
    setCacheOrderList(cacheOrderList.filter((item) => item.jobId !== orderId));
    shippingEditCancel(orderId);
    setFormData(formListClone);
    setCheckOrderId(undefined);
    setFormQuantityValues({});
  };

  const updateItemLine = async (
    orderId: number,
    orderIdx: number,
    formListClone: PurchaseOrderJob[],
    formRes: any
  ) => {
    formListClone[orderIdx].shippingItems.map(
      (item, idx, arr) => (arr[idx] = { ...item, ...formRes[+orderId][idx] })
    );
    const payloadData: ShippingSplitPayload = {
      jobId: formListClone[orderIdx].jobId,
      shippingItems: [],
    };
    formListClone[orderIdx].shippingItems.forEach((item) => {
      payloadData.shippingItems.push({
        serialNumber: item?.serialNumber,
        shipmentType: carrierService.includes(
          item.carrier?.carrierService || ''
        )
          ? ShipmentTypeEnum.PACKAGE
          : ShipmentTypeEnum.FREIGHT,
        quantity: item.quantity || 0,
        itemQuantity: item.quantity || 0,
        carrierType: item.carrier?.carrierType,
        carrierService: item.carrier?.carrierService,
        toAddressLine: item.shippingInfo.toAddressLine,
        toCity: item.shippingInfo.toCity,
        toCountryCode: item.shippingInfo.toCountryCode,
        toName: item.shippingInfo.toName,
        toPostalCode: item.shippingInfo.toPostalCode,
        toStateProvinceCode: item.shippingInfo.toStateProvinceCode,
        accountType:
          item.carrier?.accountType || ShippingAccountTypeEnum.REVSHOP,
      });
    });
    if (typeof onChange === 'function') {
      const res = await onChange(payloadData);
      if (res === 'success') {
        setCacheOrderList(
          cacheOrderList.filter((item) => item.jobId !== orderId)
        );
        shippingEditCancel(orderId);
        setCheckOrderId(undefined);
      } else {
        // cancelItemLine(orderId, orderIdx);
      }
      // setFormData(formListClone || []);
    } else {
      // cancelItemLine(orderId, orderIdx);
    }
  };

  const updateItemLineModal = async (
    orderId: number,
    orderIdx: number,
    quantityInconsistent: boolean
  ) => {
    setCheckOrderId(orderId);
    const validateArr = lineItemValidate(orderId, orderIdx);
    const formListClone = cloneDeep(formList);
    try {
      const formRes = await form.validateFields(validateArr);
      if (quantityInconsistent) {
        confirm({
          title: 'Your split shippings aren’t adding up to the total.',
          okText: 'OK, recheck',
          cancelText: 'Update total quantity',
          centered: true,
          onOk() {},
          onCancel() {
            updateItemLine(orderId, orderIdx, formListClone, formRes);
          },
        });
      } else {
        updateItemLine(orderId, orderIdx, formListClone, formRes);
      }
    } catch {
      const errorItem = form
        .getFieldsError()
        .filter((item) => item.errors.length)[0];
      if (errorItem && errorItem.errors.length) {
        const id = `quantity${errorItem.name[errorItem.name.length - 2]}`;
        const itemCoordinate = document
          .getElementById(id)
          ?.getBoundingClientRect();
        if (itemCoordinate) {
          window.scrollTo({
            left: itemCoordinate?.left,
            top: +itemCoordinate?.top + 240,
          });
        }
      }
    }
  };

  const shippingLineItemAdd = (orderId: number, orderIdx: number) => {
    const formListClone = cloneDeep(formList);
    const validateArr = lineItemValidate(orderId, orderIdx);
    const formRes = form.getFieldsValue(validateArr);
    formListClone[orderIdx].shippingItems.map(
      (item, idx, arr) => (arr[idx] = { ...item, ...formRes[+orderId][idx] })
    );
    formListClone[orderIdx].shippingItems.push({
      ...defaultShippingLineItem,
      weight: formListClone[orderIdx].shippingItems[0].weight,
    });
    setFormData(formListClone);
  };

  const splitLineItem = (orderId: number, orderIdx: number) => {
    editLineItem(orderId);
    shippingLineItemAdd(orderId, orderIdx);
  };

  const formQuantityMemo = (
    allValues: IFormData,
    formCacheList?: PurchaseOrderJob[]
  ) => {
    const formQuantity: IFormQuantity = {};
    const formListClone = formCacheList || cloneDeep(formList);
    for (const i in allValues) {
      const formListCloneJob = formListClone.filter(
        (item) => item.jobId === +i
      )[0];
      formListCloneJob.shippingItems.map((item, idx, arr) => {
        let choiceCarrier = allValues[i][idx].carrier;
        if (
          choiceCarrier?.carrierService &&
          choiceCarrier.carrierType &&
          choiceCarrier.accountType
        ) {
          if (
            (formListCloneJob.weightUnit * (allValues[i][idx].quantity || 0) >
              150 &&
              carrierService.includes(
                allValues[i][idx].carrier?.carrierService || ''
              )) ||
            (formListCloneJob.weightUnit * (allValues[i][idx].quantity || 0) <
              150 &&
              otherCarrierService.includes(
                allValues[i][idx].carrier?.carrierService || ''
              ))
          ) {
            choiceCarrier = {
              ...choiceCarrier,
              carrierService: '',
            };
          }
        }
        return (arr[idx] = {
          ...item,
          ...allValues[i][idx],
          carrier: choiceCarrier,
        });
      });
      formQuantity['' + i] =
        allValues[i]
          .map((item) => item.quantity)
          .reduce((a, b) => (a || 0) + (b || 0), 0) || 0;
    }
    setFormData(formListClone);
    setFormQuantityValues(formQuantity);
  };

  const onValuesChange = (
    changedValues: { email?: string },
    allValues: IFormData
  ) => {
    formQuantityMemo(allValues);
  };

  const checkQuantity = (_: any, value: number, orderId: number) => {
    if (value || checkOrderId !== orderId) {
      return Promise.resolve();
    }
    return Promise.reject(t('common.quantityValidate'));
  };

  const checkAddress = (
    _: any,
    value: TemporaryShippingAddressValue,
    orderId: number
  ) => {
    if ((value.toName && value.toAddressLine) || checkOrderId !== orderId) {
      return Promise.resolve();
    }
    return Promise.reject(
      t('workspace.detail.shippingBilling.shippingAddressValidate')
    );
  };

  const checkShippingUps = (
    _: any,
    value: TemporaryShippingUpsValue,
    orderId: number
  ) => {
    if (
      (value && value.carrierService && value.carrierType) ||
      checkOrderId !== orderId
    ) {
      return Promise.resolve();
    }
    return Promise.reject(
      t('workspace.detail.shippingBilling.shippingUpsValidate')
    );
  };

  return (
    <>
      <Form
        form={form}
        className="shipping-form"
        onValuesChange={onValuesChange}
      >
        {formList.map((formItem, orderIdx) => {
          const orderId = formItem.jobId;
          const shippingQuantityTotal =
            formQuantityValues[orderId] ||
            formItem.shippingItems
              .map((item) => item.quantity)
              .reduce((a, b) => (a || 0) + (b || 0), 0);
          return (
            <React.Fragment key={`order${orderId}${orderIdx}`}>
              {isEdit(orderId) && (
                <div
                  className="shipping-form-header"
                  hidden={formItem.shippingItems.length === 1}
                >
                  <span>{t('platform.shipping.totalQuantity')}:</span>
                  <p>
                    <span
                      className={
                        shippingQuantityTotal !== formItem.quantity
                          ? 'error-quantity'
                          : ''
                      }
                    >
                      {shippingQuantityTotal}
                    </span>
                    {` / `}
                    {formItem.quantity}
                  </p>
                  <label></label>
                  <p
                    hidden={
                      shippingQuantityTotal === formItem.quantity ||
                      formItem.shippingItems.length === 1
                    }
                  >
                    <span className="error-quantity-tip">
                      {t('platform.shipping.errorQuantityTip')}
                    </span>
                  </p>
                </div>
              )}
              <div className="shipping-form-split">
                <div className="shipping-form-split-container">
                  <div
                    className="shipping-form-split-container-tag"
                    hidden={isEdit(orderId)}
                  ></div>
                  <Form.List name={orderId}>
                    {(fields, { add, remove }) => (
                      <>
                        {fields.map((field) => {
                          const shippingItem =
                            formItem.shippingItems[field.fieldKey] ?? {};
                          const internalQuantity =
                            shippingItem?.shippingInfo?.internalDistribution
                              ?.quantity;
                          const internalDistribution =
                            shippingItem?.shippingInfo.internalDistribution
                              ?.distribution;
                          const shippingInternalDistributionRender = [
                            internalQuantity
                              ? `QTY: ${internalQuantity}`
                              : null,
                            internalDistribution
                              ? `${internalDistribution}`
                              : null,
                          ]
                            .filter((item) => item)
                            .join(' | ');
                          return (
                            <React.Fragment key={field.fieldKey}>
                              <div
                                className={`shipping-form-item ${
                                  isEdit(orderId)
                                    ? 'shipping-form-item-active'
                                    : ''
                                }`}
                                key={orderIdx + '' + field.fieldKey}
                              >
                                <div
                                  className="shipping-form-item-title"
                                  id={field.fieldKey + 'name'}
                                >
                                  <Form.Item
                                    {...field}
                                    key={`name${orderIdx}`}
                                    name={[field.name, 'name']}
                                    fieldKey={[field.fieldKey, 'name']}
                                    className={'ant-form-item-exhibit'}
                                  >
                                    {
                                      <SectionHeading
                                        className={
                                          formItem.jobName
                                            .split(' ')
                                            .find((item) => item.length > 19)
                                            ? 'shipping-form-word-break-all'
                                            : 'shipping-form-word-keep-all'
                                        }
                                      >
                                        {formItem.jobName}
                                      </SectionHeading>
                                    }
                                  </Form.Item>
                                </div>
                                <div
                                  className="shipping-form-item-quantity"
                                  id={field.fieldKey + 'quantity'}
                                >
                                  {/* onClick={() => editItem('quantity', orderIdx)} */}
                                  <label>
                                    {t(
                                      'workspace.detail.shippingBilling.quantity'
                                    )}
                                    :
                                  </label>
                                  <Form.Item
                                    {...field}
                                    key={`quantity${
                                      orderIdx + '' + field.fieldKey
                                    }`}
                                    name={[field?.fieldKey, 'quantity']}
                                    fieldKey={[field?.fieldKey, 'quantity']}
                                    className={''}
                                    rules={[
                                      () => ({
                                        validator(_, value) {
                                          return checkQuantity(
                                            _,
                                            value,
                                            orderId
                                          );
                                        },
                                      }),
                                    ]}
                                  >
                                    {isEdit(orderId) ? (
                                      <RevxInputNumber
                                        id={`quantity${field.fieldKey}`}
                                        isInteger
                                        min={1}
                                        onChange={(e) => e}
                                        onBlur={(e) => e}
                                        value={1}
                                        maxLength={5}
                                      />
                                    ) : (
                                      <SectionHeading>
                                        {shippingItem?.quantity}
                                      </SectionHeading>
                                    )}
                                  </Form.Item>
                                </div>
                                <div
                                  className="shipping-form-item-address"
                                  id={field.fieldKey + 'address'}
                                >
                                  <label>
                                    {t(
                                      'workspace.detail.shippingBilling.sendToNameAddress'
                                    )}
                                    :
                                  </label>
                                  <Form.Item
                                    {...field}
                                    key={`shippingInfo${
                                      orderIdx + field.fieldKey
                                    }`}
                                    name={[field.name, 'shippingInfo']}
                                    fieldKey={[field.fieldKey, 'shippingInfo']}
                                    className={
                                      isEdit(orderId)
                                        ? 'shipping-form-hide-label'
                                        : ''
                                    }
                                    rules={[
                                      () => ({
                                        validator(_, value) {
                                          return checkAddress(
                                            _,
                                            value,
                                            orderId
                                          );
                                        },
                                      }),
                                    ]}
                                    id={`shippingInfo${field.fieldKey}`}
                                  >
                                    {isEdit(orderId) ? (
                                      <TemporaryShippingAddress
                                        contactPlaceholder={t(
                                          'purchaseOrder.contentPlaceholder'
                                        )}
                                        addressPlaceholder={t(
                                          'purchaseOrder.addressPlaceholder'
                                        )}
                                        orgId={user?.orgId || 4}
                                        {...field}
                                      />
                                    ) : (
                                      <p>
                                        <TextHeavy>
                                          <Tag>
                                            {shippingItem?.shippingInfo?.toName}
                                          </Tag>
                                          <br />
                                          {[
                                            shippingItem?.shippingInfo
                                              ?.toAddressLine,
                                            shippingItem?.shippingInfo?.toCity,
                                            shippingItem?.shippingInfo
                                              ?.toStateProvinceCode,
                                            shippingItem?.shippingInfo
                                              ?.toPostalCode,
                                            shippingItem?.shippingInfo
                                              ?.toCountryCode,
                                          ]
                                            .filter((item) => item)
                                            .join(', ')}
                                        </TextHeavy>
                                      </p>
                                    )}
                                  </Form.Item>
                                  {Boolean(
                                    shippingInternalDistributionRender
                                  ) && (
                                    <>
                                      <label>
                                        {t(
                                          'workspace.detail.shippingBilling.internalDistribution'
                                        )}
                                        :
                                      </label>
                                      <div
                                        className="ant-form-item ant-typography revx-text-heavy"
                                        style={{
                                          marginBottom: 0,
                                        }}
                                      >
                                        {shippingInternalDistributionRender}
                                      </div>
                                    </>
                                  )}
                                </div>
                                <div
                                  className="shipping-form-item-shipped"
                                  id={field.fieldKey + 'shipped'}
                                >
                                  <label>
                                    {isEdit(orderId)
                                      ? t(
                                          'workspace.detail.shippingBilling.shippingOption'
                                        )
                                      : t(
                                          'workspace.detail.shippingBilling.deliveryBy'
                                        )}
                                    :
                                  </label>
                                  <Form.Item
                                    {...field}
                                    key={`carrier${orderIdx}`}
                                    name={[field.name, 'carrier']}
                                    fieldKey={[field.fieldKey, 'carrier']}
                                    className={
                                      isEdit(orderId)
                                        ? 'ant-form-item-exhibit'
                                        : ''
                                    }
                                    rules={[
                                      () => ({
                                        validator(_, value) {
                                          return checkShippingUps(
                                            _,
                                            value,
                                            orderId
                                          );
                                        },
                                      }),
                                    ]}
                                    id={`carrier${field.fieldKey}`}
                                  >
                                    {isEdit(orderId) ? (
                                      <TemporaryShippingUps
                                        carrierTypeList={carrierType}
                                        accountTypeList={accountTypeList}
                                        carrierServiceList={
                                          (shippingItem?.quantity || 1) *
                                            formItem.weightUnit <
                                          150
                                            ? carrierService
                                            : otherCarrierService
                                        }
                                        upsTypePlaceholder={t(
                                          'purchaseOrder.upsTypePlaceholder'
                                        )}
                                        upsServicePlaceholder={t(
                                          'purchaseOrder.upsServicePlaceholder'
                                        )}
                                        {...field}
                                      />
                                    ) : (
                                      <TextHeavy>
                                        <p>
                                          <RevxDateWeek
                                            dateStr={
                                              shippingItem?.carrier
                                                ?.estimatedDeliveryTime || ''
                                            }
                                          />
                                        </p>
                                        <p>
                                          <RevxDate
                                            dateStr={
                                              shippingItem?.carrier
                                                ?.estimatedDeliveryTime || ''
                                            }
                                          />
                                        </p>
                                        <p>
                                          {[
                                            shippingItem?.carrier?.carrierType,
                                            shippingItem?.carrier
                                              ?.carrierService,
                                            accountTypeList.find(
                                              (item) =>
                                                item.value ===
                                                shippingItem?.carrier
                                                  ?.accountType
                                            )?.name,
                                          ]
                                            .filter((item) => item)
                                            .join(', ')}
                                        </p>
                                      </TextHeavy>
                                    )}
                                  </Form.Item>
                                </div>
                                <div
                                  className={`shipping-form-item-printing ${
                                    isEdit(orderId) &&
                                    formItem.shippingItems.length > 1
                                      ? 'shipping-form-item-printing-split'
                                      : ''
                                  }`}
                                >
                                  {isEdit(orderId) &&
                                  formItem.shippingItems.length > 1 ? (
                                    <>
                                      <UnderlineText
                                        color="blue"
                                        onClick={() =>
                                          deleteItemLine(
                                            orderId,
                                            orderIdx,
                                            field.fieldKey
                                          )
                                        }
                                      >
                                        <span>
                                          {t(
                                            'workspace.detail.shippingBilling.delete'
                                          )}
                                        </span>
                                      </UnderlineText>
                                    </>
                                  ) : (
                                    <>
                                      <Form.Item
                                        {...field}
                                        label={
                                          <span>
                                            {t(
                                              'workspace.detail.shippingBilling.formPrinting'
                                            )}
                                          </span>
                                        }
                                        key={`printing${orderIdx}`}
                                        name={[field.name, 'printing']}
                                        fieldKey={[field.fieldKey, 'printing']}
                                        className={'ant-form-item-exhibit'}
                                      >
                                        {
                                          <TextHeavy>
                                            <span>
                                              {transformValue(
                                                <RevxMoney
                                                  value={
                                                    shippingItem?.costLineItems
                                                      ?.revxPrintingCharge
                                                  }
                                                />
                                              )}
                                            </span>
                                          </TextHeavy>
                                        }
                                      </Form.Item>
                                      {!formItem.hideFreightCost && (
                                        <Form.Item
                                          {...field}
                                          label={
                                            <span>
                                              {t(
                                                'workspace.detail.shippingBilling.formFreight'
                                              )}
                                            </span>
                                          }
                                          key={`freight${orderIdx}`}
                                          name={[field.name, 'freight']}
                                          fieldKey={[field.fieldKey, 'freight']}
                                          className={'ant-form-item-exhibit'}
                                        >
                                          {
                                            <>
                                              <TextHeavy>
                                                <span data-testid="text-shipping-item-freight">
                                                  {transformValue(
                                                    <RevxMoney
                                                      value={
                                                        shippingItem?.carrier
                                                          ?.accountType ===
                                                          ShippingAccountTypeEnum.ORGANIZATION &&
                                                        shippingItem?.carrier
                                                          ?.carrierType ===
                                                          CarrierTypeEnum.UPS
                                                          ? shippingItem
                                                              ?.costLineItems
                                                              ?.shippingCost
                                                          : shippingItem
                                                              ?.costLineItems
                                                              ?.revxFreightCharge
                                                      }
                                                    />
                                                  )}
                                                </span>
                                              </TextHeavy>
                                              {shippingItem?.carrier
                                                ?.carrierType ===
                                                CarrierTypeEnum.UPS &&
                                              shippingItem?.carrier
                                                ?.accountType ===
                                                ShippingAccountTypeEnum.ORGANIZATION
                                                ? ` (billed separately)`
                                                : ''}
                                            </>
                                          }
                                        </Form.Item>
                                      )}
                                      <div className="shipping-form-item-printing-total">
                                        <Form.Item
                                          {...field}
                                          label={
                                            <label>
                                              {t(
                                                'workspace.detail.shippingBilling.formSubtotal'
                                              )}
                                            </label>
                                          }
                                          key={`totalPrice${orderIdx}`}
                                          name={[field.name, 'totalPrice']}
                                          fieldKey={[
                                            field.fieldKey,
                                            'totalPrice',
                                          ]}
                                          className={'ant-form-item-exhibit'}
                                        >
                                          {
                                            <span data-testid="text-shipping-item-subtotal">
                                              {transformValue(
                                                <RevxMoney
                                                  value={
                                                    formItem.hideFreightCost
                                                      ? shippingItem?.costLineItems
                                                          ?.revxPrintingCharge
                                                      : shippingItem?.costLineItems
                                                          ?.revxTotalCharge
                                                  }
                                                />
                                              )}
                                            </span>
                                          }
                                        </Form.Item>
                                      </div>
                                    </>
                                  )}
                                </div>
                              </div>
                            </React.Fragment>
                          );
                        })}
                      </>
                    )}
                  </Form.List>
                </div>
                <div
                  className={`shipping-form-split-action ${
                    isEdit(orderId) ? 'shipping-form-split-action-active' : ''
                  }`}
                  hidden={hideAction}
                >
                  {isEdit(orderId) ? (
                    <>
                      <RevxButton
                        type="primary"
                        size="large"
                        onClick={() =>
                          updateItemLineModal(
                            orderId,
                            orderIdx,
                            shippingQuantityTotal !== formItem.quantity &&
                              formItem.shippingItems.length !== 1
                          )
                        }
                      >
                        {t('workspace.detail.shippingBilling.update')}
                      </RevxButton>
                      <RevxButton
                        size="large"
                        onClick={() => cancelItemLine(orderId, orderIdx)}
                      >
                        {t('common.cancel')}
                      </RevxButton>
                    </>
                  ) : (
                    <div className="shipping-form-item-action">
                      <EditIcon className="anticon-edit" />
                      <UnderlineText
                        color="blue"
                        onClick={() => editLineItem(orderId)}
                      >
                        <span>
                          {t('workspace.detail.shippingBilling.edit')}
                        </span>
                      </UnderlineText>
                      {formItem.shippingItems.length < 10 && (
                        <UnderlineText
                          color="blue"
                          onClick={() => splitLineItem(orderId, orderIdx)}
                        >
                          <span>
                            {t('workspace.detail.shippingBilling.split')}
                          </span>
                        </UnderlineText>
                      )}
                    </div>
                  )}
                </div>
              </div>
              {isEdit(orderId) &&
                formItem.shippingItems.length < 10 &&
                formItem.shippingItems.length > 1 && (
                  <div className="shipping-form-action">
                    <div onClick={() => shippingLineItemAdd(orderId, orderIdx)}>
                      <PlusCircleOutlined />
                      <span>{t('workspace.detail.shippingBilling.add')}</span>
                    </div>
                  </div>
                )}
            </React.Fragment>
          );
        })}
      </Form>
    </>
  );
};
